import { SignInButton, SignedIn, SignedOut, useAuth } from "@clerk/clerk-react";
import { Link } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { resolveClerkPublishableKey } from "../clerk-env";
import {
  createCustomerSavedAddress,
  deleteCustomerSavedAddress,
  fetchCustomerSavedAddresses,
  patchCustomerSavedAddress,
  type CustomerSavedAddress,
} from "../lib/fetch-customer-saved-addresses";
import { fetchMe, formatMeFetchError } from "../lib/fetch-with-clerk";

const MAX_SAVED = 5;
const ACTION_FEEDBACK_AUTODISMISS_MS = 3_000;

function formatAddressLines(a: CustomerSavedAddress): string {
  const line2 = a.addressLine2 ? `, ${a.addressLine2}` : "";
  return `${a.addressLine1}${line2}, ${a.city}, ${a.state} ${a.zip}`;
}

function Header() {
  return (
    <header className="space-y-2 border-b border-[var(--card-border)] pb-6">
      <p className="text-[10px] font-semibold uppercase tracking-[.1em] text-[var(--green)]">Account</p>
      <h1 className="text-2xl font-bold tracking-tight text-[var(--text-primary)]">Saved addresses</h1>
      <p className="text-sm text-[var(--text-secondary)]">
        Up to {MAX_SAVED} Colorado delivery addresses. The default syncs to your profile for checkout.
      </p>
    </header>
  );
}

export function AccountAddressesScreen() {
  const clerkPk = resolveClerkPublishableKey();
  if (!clerkPk) {
    return (
      <div className="mx-auto w-full max-w-[1200px] space-y-6 pt-6 pb-16">
        <Header />
        <div className="rounded-2xl border border-[var(--card-border)] bg-white p-6 shadow-cardMd">
          <p className="text-sm text-amber-900">
            Set <span className="font-mono">VITE_CLERK_PUBLISHABLE_KEY</span> to manage addresses.
          </p>
        </div>
      </div>
    );
  }
  return <AccountAddressesInner />;
}

const inputCls =
  "w-full border-0 border-b border-[var(--card-border)] bg-transparent px-0 py-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-placeholder)] focus:border-[var(--green)] focus:outline-none";

function AccountAddressesInner() {
  const { getToken, isLoaded, isSignedIn } = useAuth();
  const [profileError, setProfileError] = useState<string | null>(null);
  const [addresses, setAddresses] = useState<CustomerSavedAddress[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionOk, setActionOk] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const [addLabel, setAddLabel] = useState("Home");
  const [addLine1, setAddLine1] = useState("");
  const [addLine2, setAddLine2] = useState("");
  const [addCity, setAddCity] = useState("");
  const [addZip, setAddZip] = useState("");
  const [addDefault, setAddDefault] = useState(true);
  const [adding, setAdding] = useState(false);

  const reload = useCallback(async () => {
    const data = await fetchCustomerSavedAddresses(getToken);
    setAddresses(data.addresses);
  }, [getToken]);

  useEffect(() => {
    if (!isLoaded || !isSignedIn) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setProfileError(null);
    void (async () => {
      try {
        const me = await fetchMe(getToken);
        if (cancelled) return;
        if (!me.user.roles.includes("customer") || !me.user.customerId) {
          setProfileError(
            !me.user.roles.includes("customer")
              ? `This account does not have the customer role (primary role shown as "${me.user.role}").`
              : "No customer profile id — sign out and back in, or confirm API auto-provisioning.",
          );
          setLoading(false);
          return;
        }
        const data = await fetchCustomerSavedAddresses(getToken);
        if (cancelled) return;
        setAddresses(data.addresses);
      } catch (e) {
        if (!cancelled) {
          if (e instanceof Error && /^saved_addresses_http_(\d+)$/.test(e.message)) {
            const code = Number(/^saved_addresses_http_(\d+)$/.exec(e.message)?.[1]);
            setProfileError(
              code === 403
                ? "This session cannot load saved addresses (403). Use a shopper account with role customer."
                : code === 404
                  ? "Saved-address API not found (404). Restart the API with the latest code and apply migration 013-customer-saved-addresses.sql."
                  : `Could not load addresses (HTTP ${code}).`,
            );
          } else {
            setProfileError(formatMeFetchError(e));
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isLoaded, isSignedIn, getToken]);

  /** Auto-dismiss success feedback so it doesn't pile up. Errors stay until
   *  the user takes another action. */
  useEffect(() => {
    if (!actionOk) return;
    const id = window.setTimeout(() => setActionOk(null), ACTION_FEEDBACK_AUTODISMISS_MS);
    return () => window.clearTimeout(id);
  }, [actionOk]);

  async function onAddSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (addresses == null) return;
    if (addresses.length >= MAX_SAVED) {
      setActionError(`You can save up to ${MAX_SAVED} addresses. Remove one to add another.`);
      return;
    }
    setActionError(null);
    setActionOk(null);
    setAdding(true);
    try {
      await createCustomerSavedAddress(getToken, {
        label: addLabel.trim() || "Home",
        addressLine1: addLine1.trim(),
        addressLine2: addLine2.trim() || undefined,
        city: addCity.trim(),
        zip: addZip.trim(),
        isDefault: addDefault,
      });
      setAddLine1("");
      setAddLine2("");
      setAddCity("");
      setAddZip("");
      setAddLabel("Home");
      setAddDefault(addresses.length === 0);
      await reload();
      setActionOk("Address added.");
    } catch (err) {
      setActionError(formatActionError(err));
    } finally {
      setAdding(false);
    }
  }

  async function onSetDefault(id: string) {
    setActionError(null);
    setActionOk(null);
    setBusyId(id);
    try {
      await patchCustomerSavedAddress(getToken, id, { isDefault: true });
      await reload();
      setActionOk("Default updated.");
    } catch (err) {
      setActionError(formatActionError(err));
    } finally {
      setBusyId(null);
    }
  }

  async function onDelete(id: string) {
    if (!window.confirm("Remove this saved address?")) return;
    setActionError(null);
    setActionOk(null);
    setBusyId(id);
    try {
      await deleteCustomerSavedAddress(getToken, id);
      await reload();
      setActionOk("Address removed.");
    } catch (err) {
      setActionError(formatActionError(err));
    } finally {
      setBusyId(null);
    }
  }

  const isEmpty = addresses != null && addresses.length === 0;
  const canAddMore = addresses != null && addresses.length < MAX_SAVED;

  return (
    <div className="mx-auto w-full max-w-[1200px] space-y-6 pt-6 pb-16">
      <Header />

      <SignedOut>
        <div className="rounded-2xl border border-[var(--card-border)] bg-white p-6 text-center shadow-cardMd">
          <p className="text-sm text-[var(--text-secondary)]">Sign in to save and manage addresses.</p>
          <SignInButton mode="modal">
            <button
              type="button"
              className="mt-4 inline-flex items-center rounded-lg bg-[var(--green)] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[var(--green-dark)]"
            >
              Sign in
            </button>
          </SignInButton>
        </div>
      </SignedOut>

      <SignedIn>
        {loading ? (
          <p className="text-sm text-[var(--text-muted)]">Loading…</p>
        ) : profileError ? (
          <div
            role="alert"
            className="rounded-2xl border border-[var(--badge-red)] bg-[var(--status-error-bg)] p-4 text-sm text-[var(--status-error-text)]"
          >
            {profileError}
          </div>
        ) : (
          <>
            {actionError ? (
              <div
                role="alert"
                className="rounded-2xl border border-[var(--badge-red)] bg-[var(--status-error-bg)] p-4 text-sm text-[var(--status-error-text)]"
              >
                {actionError}
              </div>
            ) : null}
            {actionOk ? (
              <div
                role="status"
                className="inline-flex items-center rounded-full bg-[var(--surface-sage)] px-4 py-1.5 text-sm font-medium text-[var(--green-dark)]"
              >
                {actionOk}
              </div>
            ) : null}

            {addresses != null && addresses.length > 0 ? (
              <ul className="grid gap-3 md:grid-cols-2">
                {addresses.map((a) => (
                  <li
                    key={a.id}
                    className="space-y-2 rounded-2xl border border-[var(--card-border)] bg-white p-5 shadow-cardMd"
                  >
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div>
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="font-semibold text-[var(--text-primary)]">{a.label}</p>
                          {a.isDefault ? (
                            <span className="inline-flex items-center rounded-full bg-[var(--surface-sage)] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[var(--green-dark)]">
                              Default
                            </span>
                          ) : null}
                        </div>
                        <p className="mt-1 text-sm text-[var(--text-secondary)]">{formatAddressLines(a)}</p>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-3 pt-1 text-xs">
                      {!a.isDefault ? (
                        <button
                          type="button"
                          disabled={busyId != null}
                          onClick={() => void onSetDefault(a.id)}
                          className="font-medium text-[var(--text-secondary)] transition-colors hover:text-[var(--green)] disabled:opacity-40"
                        >
                          {busyId === a.id ? "…" : "Set default"}
                        </button>
                      ) : null}
                      <button
                        type="button"
                        disabled={busyId != null}
                        onClick={() => void onDelete(a.id)}
                        className="font-medium text-[var(--badge-red)] transition-colors hover:text-red-700 disabled:opacity-40"
                      >
                        {busyId === a.id ? "…" : "Remove"}
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            ) : isEmpty ? (
              /* Per redline 08 flag 6: when list is empty, collapse the
               *  empty-state card into a small inline heading above the
               *  always-visible Add form below. The form IS the affordance. */
              <p className="text-sm font-medium text-[var(--text-secondary)]">
                No saved addresses yet — add your first below.
              </p>
            ) : null}

            {canAddMore ? (
              <form
                onSubmit={(e) => void onAddSubmit(e)}
                className="max-w-2xl space-y-4 rounded-2xl border border-[var(--card-border)] bg-white p-5 shadow-cardMd"
              >
                <p className="text-[10px] font-semibold uppercase tracking-[.1em] text-[var(--text-secondary)]">
                  Add address
                </p>
                <FieldLabel label="Label">
                  <input
                    value={addLabel}
                    onChange={(e) => setAddLabel(e.target.value)}
                    className={inputCls}
                    placeholder="Home, Work…"
                  />
                </FieldLabel>
                <FieldLabel label="Street">
                  <input
                    value={addLine1}
                    onChange={(e) => setAddLine1(e.target.value)}
                    autoComplete="street-address"
                    className={inputCls}
                    required
                  />
                </FieldLabel>
                <FieldLabel label="Apt / suite (optional)">
                  <input
                    value={addLine2}
                    onChange={(e) => setAddLine2(e.target.value)}
                    className={inputCls}
                  />
                </FieldLabel>
                <FieldLabel label="City">
                  <input
                    value={addCity}
                    onChange={(e) => setAddCity(e.target.value)}
                    className={inputCls}
                    required
                  />
                </FieldLabel>
                <div className="grid grid-cols-3 gap-3">
                  <FieldLabel label="State">
                    <input value="CO" readOnly className={`${inputCls} text-[var(--text-muted)]`} />
                  </FieldLabel>
                  <FieldLabel label="ZIP" className="col-span-2">
                    <input
                      value={addZip}
                      onChange={(e) => setAddZip(e.target.value)}
                      inputMode="numeric"
                      autoComplete="postal-code"
                      className={inputCls}
                      placeholder="80202"
                      required
                    />
                  </FieldLabel>
                </div>
                <label className="flex items-center gap-2 text-sm text-[var(--text-secondary)]">
                  <input
                    type="checkbox"
                    checked={addDefault}
                    onChange={(e) => setAddDefault(e.target.checked)}
                    className="rounded border-[var(--card-border)] text-[var(--green)] focus:ring-[var(--green)]"
                  />
                  Save as default delivery address
                </label>
                <button
                  type="submit"
                  disabled={adding}
                  className="inline-flex items-center rounded-lg bg-[var(--green)] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[var(--green-dark)] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {adding ? "Saving…" : "Save address"}
                </button>
              </form>
            ) : null}
          </>
        )}
      </SignedIn>

      <div className="border-t border-[var(--card-border)] pt-6">
        <Link
          to="/"
          className="text-sm font-medium text-[var(--text-secondary)] transition-colors hover:text-[var(--green)]"
        >
          ← Back to marketplace
        </Link>
      </div>
    </div>
  );
}

function FieldLabel({
  label,
  className = "",
  children,
}: {
  label: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <label className={`block ${className}`}>
      <span className="block pb-0.5 text-[10px] font-semibold uppercase tracking-wide text-[var(--text-secondary)]">
        {label}
      </span>
      {children}
    </label>
  );
}

function formatActionError(err: unknown): string {
  if (!(err instanceof Error)) {
    return "Something went wrong. Try again.";
  }
  const create = /^saved_address_create_http_(\d+)$/.exec(err.message);
  if (create) {
    const code = Number(create[1]);
    if (code === 409) {
      return `You already have ${MAX_SAVED} saved addresses. Remove one before adding another.`;
    }
    if (code === 422) {
      return "That address is not allowed for Colorado delivery (private residence + permitted ZIP).";
    }
    if (code === 400) {
      return "Check the ZIP code format (five digits).";
    }
    return `Could not save (HTTP ${code}).`;
  }
  const patch = /^saved_address_patch_http_(\d+)$/.exec(err.message);
  if (patch) {
    return `Could not update address (HTTP ${patch[1]}).`;
  }
  const del = /^saved_address_delete_http_(\d+)$/.exec(err.message);
  if (del) {
    return `Could not remove address (HTTP ${del[1]}).`;
  }
  const list = /^saved_addresses_http_(\d+)$/.exec(err.message);
  if (list) {
    const code = Number(list[1]);
    if (code === 404) {
      return "Saved-address API not found (404). Restart the API and apply migration 013-customer-saved-addresses.sql.";
    }
    return `Could not load addresses (HTTP ${code}).`;
  }
  return err.message;
}
